import { TableProps, Spin } from 'antd';
import { observer } from 'mobx-react-lite';
import { SorterResult } from 'antd/lib/table/interface';
import { useEffect, useState, useMemo } from 'react';

import { Layout, Table } from 'Components';
import { createSortDescription } from 'Components/Table/createColumns';
import ModalContextProvider from 'Hooks/useModal';
import { useStores } from 'Hooks/useStore';
import { SavedReport } from 'Services/Api/Reports/Types';
import { getIsPending } from 'Stores/util';
import useColumns, { TableFilterValues } from './columns/columns';
import ReportModals from './ReportModals/ReportModals';
import styles from '../Reports.module.scss';
import EmptyState from 'PhishingManager/components/EmptyState';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from 'Hooks';
import MobileView from './columns/MobileView';
import DebounceInput from 'PhishingManager/components/DebouncedInput';
import { useDownloadReport } from 'Hooks/useDownloadReportProgress/useDownloadReport';
import PaginationElement from 'PhishingManager/components/Pagination';

const PAGE_SIZE = 10;
function Saved() {
	const [search, setSearch] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const { authStore, reportsStore } = useStores();
	const { savedReports, status, savedReportsList } = reportsStore;
	const { t } = useTranslation();
	const isMobile = useIsMobile(); 
	const { loading: pdfLoading } = useDownloadReport();

	const tableData : SavedReport[] = useMemo(() => {
        const startIdx = PAGE_SIZE * (currentPage - 1);
        return isMobile ? savedReportsList.slice(0, startIdx + PAGE_SIZE) : savedReportsList.slice(startIdx, startIdx + PAGE_SIZE)
    }, [currentPage, savedReportsList, isMobile]);

	useEffect(() => {
		reportsStore.getSavedReports({...savedReports.params, reportName: search || undefined });
	}, [search])

	useEffect(() => {
		return () => reportsStore.clearSavedReports()
	}, []);

	const handleTable: TableProps<SavedReport>['onChange'] = (
		_,
		filters,
		sorter
	) => {
		const sort = createSortDescription<SavedReport>(
			sorter as SorterResult<SavedReport>
		);
		const values = filters as TableFilterValues;

		reportsStore.getSavedReports({
			...savedReports.params,
			sort,
			reportName: values.reportName?.[0] ?? undefined,
			reportTypes: values.reportTypes,
			recurrenceTypes: values.recurrenceTypes,
			emailsList: values.emailsList?.[0] ?? undefined
		});
	};

	const refresh = () => {
		reportsStore.getSavedReports();
	};

	const columns = useColumns({
		params: savedReports.params,
		currentUserEmail: authStore.currentUser?.email ?? ''
	});

	const loading = getIsPending(status.getSavedReports);
	const showInput = savedReportsList.length || search.length;

	return (
		<ModalContextProvider>
			<Layout className={styles.saved}>
				<Spin spinning={loading || pdfLoading}>
					{showInput ? <div className={styles.inputFilter}>
						<DebounceInput placeholder={t("phishingManager.campaignSetup.step1.search")} setDebouncedSearch={setSearch} showClearOption={true} />
					</div> : null}
					{!savedReportsList.length ? <EmptyState
						title={t('reports.noData.title')}
						description={[t('reports.noData.desc')]}
						actionLabel={null} /> : isMobile ? <MobileView {...{ records: tableData, t, totalCount: savedReportsList.length, setCurrentPage: () => setCurrentPage(prev => prev + 1)}} /> :
						<>
							<Table<SavedReport>
								dataSource={tableData}
								rowKey="id"
								columns={columns}
								onChange={handleTable}
								totalVisible
								pagination={false}
							/>
							<PaginationElement
								total={savedReportsList.length}
								pageSize={PAGE_SIZE}
								current={currentPage}
								onChange={setCurrentPage}
							/>
						</>}
					<ReportModals {...{ refresh, isMobile }} />
				</Spin>
			</Layout>
		</ModalContextProvider>
	);
}

export default observer(Saved);